import React from "react";
import { Box, Button, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

const SessionControls = ({
  sessionStarted,
  isMicOn,
  toggleRecording,
  toggleCC,
  showCC,
  handleExit,
  onStart,
  showMicPrompt,
  showStartButton,
  isStartingSession,
  showControls,
  setShowEndDialog,
  t,
  ttsEnabled,
  toggleTTS,
  interactionInProgress,
  controlsDisabled,
  recordingTimeLeftMs,
}) => {
  const formatTimeLeft = (timeLeftMs) => {
    if (timeLeftMs === null || timeLeftMs === undefined) return "";
    const totalSeconds = Math.max(0, Math.ceil(timeLeftMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const showCountdown = isMicOn && recordingTimeLeftMs !== null;
  const isNearLimit = showCountdown && recordingTimeLeftMs <= 15000;
  const isTalkButtonBlocked = interactionInProgress || controlsDisabled;
  const talkButtonLabel = isMicOn
    ? t("stop_talk")
    : controlsDisabled
    ? t("session_ended")
    : interactionInProgress
    ? t("wait_short")
    : t("talk");

  const getButtonBg = () => {
    if (isTalkButtonBlocked) return "#888";
    if (isMicOn) return "#616161";
    return "#4caf50";
  };

  const getButtonHoverBg = () => {
    if (isTalkButtonBlocked) return "#888";
    if (isMicOn) return "#4e4e4e";
    return "#43a047";
  };

  const pulseColor = isNearLimit ? "rgba(229, 57, 53, 0.55)" : "rgba(76, 175, 80, 0.55)";
  const pulseKeyframes = `
    @keyframes talkButtonPulse {
      0% { box-shadow: 0 0 8px ${pulseColor}; }
      50% { box-shadow: 0 0 18px ${pulseColor}; }
      100% { box-shadow: 0 0 8px ${pulseColor}; }
    }
  `;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        mt: sessionStarted ? 4 : 0,
      }}
    >
      {sessionStarted && showControls && (
        <Button
          onClick={() => {
            if (isTalkButtonBlocked) return;
            toggleRecording();
          }}
          variant="contained"
          sx={{
            minWidth: 300,
            height: 48,
            borderRadius: "999px",
            fontWeight: 600,
            color: "#fff",
            backgroundColor: getButtonBg(),
            animation: isMicOn && !interactionInProgress && !controlsDisabled
              ? "talkButtonPulse 1.5s ease-in-out infinite"
              : "none",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              backgroundColor: getButtonHoverBg(),
            },
            display: "flex",
            gap: 1,
            justifyContent: "center",
            alignItems: "center",
            cursor: isTalkButtonBlocked ? "not-allowed" : "pointer",
          }}
        >
          {isTalkButtonBlocked && !isMicOn ? (
            <MicOffIcon sx={{ color: "#fff" }} />
          ) : (
            <MicIcon sx={{ color: isNearLimit ? "#8b1e1e" : isMicOn ? "#76ff03" : "#fff" }} />
          )}
          <Box
            component="span"
            sx={{
              color: isNearLimit ? "#ffd7b0" : "#fff",
              opacity: isTalkButtonBlocked && !isMicOn ? 0.45 : 1,
              letterSpacing: isTalkButtonBlocked && !isMicOn ? "0.1em" : "normal",
            }}
          >
            {talkButtonLabel}
          </Box>
          {showCountdown && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                minWidth: 42,
                textAlign: "right",
                color: isNearLimit ? "#ffd7b0" : "#fff",
                fontVariantNumeric: "tabular-nums",
                fontWeight: isNearLimit ? 600 : 500,
              }}
            >
              {formatTimeLeft(recordingTimeLeftMs)}
            </Box>
          )}
        </Button>
      )}
      <style>{pulseKeyframes}</style>

      {showStartButton && !sessionStarted && !showMicPrompt && (
        <Box textAlign="center" mt={4}>
          <Button
            onClick={onStart}
            disabled={isStartingSession}
            variant="contained"
            sx={{
              borderRadius: "999px",
              px: 4,
              py: 1.5,
              height: 48,
              fontWeight: 600,
              textTransform: "none",
              color: "#ffffff",
              background: "linear-gradient(to right, #5e35b1, #7e57c2)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              opacity: isStartingSession ? 0.8 : 1,
              "&:hover": {
                background: "linear-gradient(to right, #4527a0, #673ab7)",
              },
              "&.Mui-disabled": {
                color: "#ffffff",
                background: "linear-gradient(to right, #5e35b1, #7e57c2)",
                opacity: 0.8,
              },
            }}
          >
            {isStartingSession ? (t("wait_short") || "WAIT") : t("start")}
          </Button>
        </Box>
      )}

      {sessionStarted && showControls && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            onClick={() => {
              if (controlsDisabled) return;
              toggleTTS();
            }}
            sx={{
              mr: 2,
              width: 40,
              height: 40,
              borderRadius: "45%",
              backgroundColor: "rgba(0,0,0,0.4)",
              border: `2px solid ${ttsEnabled ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.2)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: controlsDisabled ? "not-allowed" : "pointer",
              opacity: controlsDisabled ? 0.5 : 1,
              transition: "all 0.2s ease-in-out",
              boxShadow: ttsEnabled ? "0 0 6px rgba(255,255,255,0.3)" : "none",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
                boxShadow: "0 0 10px rgba(255,255,255,0.4)",
              },
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: "0 0 4px rgba(255,255,255,0.2)",
              },
              "&:focus": { outline: "none" },
              outline: "none",
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
            }}
          >
            {ttsEnabled ? (
              <VolumeUpIcon sx={{ color: "#fff" }} />
            ) : (
              <VolumeOffIcon sx={{ color: "#aaa" }} />
            )}
          </Box>

          <Button
            onClick={() => setShowEndDialog(true)}
            variant="contained"
            color="error"
            sx={{ borderRadius: "999px", px: 4, py: 1.5, height: 48 }}
          >
            {t("end")}
          </Button>

          <Box
            onClick={() => {
              if (controlsDisabled) return;
              toggleCC();
            }}
            sx={{
              ml: 2,
              width: 40,
              height: 40,
              borderRadius: "45%",
              backgroundColor: "rgba(0,0,0,0.4)",
              border: `2px solid ${
                showCC ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.2)"
              }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: controlsDisabled ? "not-allowed" : "pointer",
              opacity: controlsDisabled ? 0.5 : 1,
              transition: "all 0.2s ease-in-out",
              boxShadow: showCC ? "0 0 6px rgba(255,255,255,0.3)" : "none",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
                boxShadow: "0 0 10px rgba(255,255,255,0.4)",
              },
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: "0 0 4px rgba(255,255,255,0.2)",
              },
              "&:focus": { outline: "none" },
              outline: "none",
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
            }}
          >
            <Typography
              sx={{
                color: showCC ? "#fff" : "#aaa",
                fontWeight: 600,
                fontStyle: "italic",
                fontSize: 14,
                userSelect: "none",
              }}
            >
              CC
            </Typography>
          </Box>
        </Box>
      )}

      {showStartButton && !sessionStarted && (
        <Button
          onClick={handleExit}
          variant="text"
          sx={{
            color: "#fff",
            textShadow: "0 0 4px rgba(0,0,0,0.4)",
            mt: 1,
            fontWeight: 500,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              color: "#fff",
              textShadow: "0 0 8px rgba(255,255,255,0.6)",
              transform: "scale(1.05)",
            },
            "&:active": {
              transform: "scale(0.95)",
              textShadow: "0 0 6px rgba(255,255,255,0.4)",
            },
          }}
        >
          {t("back")}
        </Button>
      )}
    </Box>
  );
};

export default SessionControls;
